/*
 * One commission payment, opened from Commissions or the Overview: what the
 * client paid, the fee the rate applies to, and when it becomes withdrawable.
 * The same facts the Transactions page shows when a row is expanded. That
 * layout is built for a wide panel, so the sheet has its own.
 */

import type { ReactNode } from "react";
import type { Commission } from "../api/client";
import { useMe } from "../auth/AuthContext";
import { COMMISSION_STATE, Dialog, Status } from "./bits";
import { fmtDate, fmtDateTime, inr, ordinal, rateLabel } from "./format";
import "./clients.css";

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export default function CommissionSheet({ c, onClose }: { c: Commission; onClose: () => void }) {
  const st = COMMISSION_STATE[c.state];
  const program = useMe().commission_program;
  return (
    <Dialog
      variant="sheet"
      title={`Commission · ${c.client_name ?? "Client"}`}
      description={<Status tone={st.tone}>{c.state === "pending" ? `Pending until ${fmtDate(c.available_at)}` : st.label}</Status>}
      onClose={onClose}
    >
      <dl className="kv client-kv">
        <Row label="Client">{c.client_name ?? "—"}</Row>
        {c.plan_name && <Row label="Plan">{c.plan_name}</Row>}
        <Row label="Invoice">{c.invoice_number ?? "—"}</Row>
        <Row label="Client paid (incl. GST)">{inr(c.payment_amount)}</Row>
        <Row label="Fee (excl. GST)">{inr(c.base_amount)}</Row>
        <Row label="Your rate">{rateLabel(c.rate)}</Row>
        <Row label="Commission">
          <strong>{inr(c.amount)}</strong>
        </Row>
        <Row label="Earned">{fmtDateTime(c.earned_at)}</Row>
        {c.state === "reversed" ? (
          <Row label="Reversed">
            {fmtDateTime(c.reversed_at)}
            {c.reversed_reason && <small>{c.reversed_reason}</small>}
          </Row>
        ) : c.state === "in_progress" ? (
          // Recorded but not payable: it is released when the partner passes the activation count.
          <Row label="Payable">
            {program ? `Once your ${ordinal(program.activation_clients + 1)} client joins` : "Not yet"}
          </Row>
        ) : (
          <Row label={c.state === "pending" ? "Withdrawable from" : "Withdrawable since"}>{fmtDate(c.available_at)}</Row>
        )}
      </dl>
    </Dialog>
  );
}
